import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useContext, useState } from 'react'
import JBWDateLabel from './JBWDateLabel'
import { AuthContext } from '../AuthContext'
import { createPotentialClaim } from '../services/ClaimsServices'

const PotentialClaimsCreateDialog = ({ claimData, invoice, onClose, openDialog }) => {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const { user } = useContext(AuthContext)

  useState(() => {
    setOpen(openDialog)
  }, [openDialog])

  const handleClose = () => {
    setOpen(false)
    setSaveError('')
    onClose()
  }

  const handleSave = () => {
    const now = new Date().toISOString()
    const payload = {
      tenantId: invoice?.tenantId,
      dealerId: invoice?.dealerId,
      invoiceId: invoice?.invoiceId,
      jobNumber: invoice?.jobNumber,
      jobId: invoice?.jobId,
      businessUnitId: invoice?.businessUnitId,
      locationId: invoice?.locationId,
      serviceDate: invoice?.stCreatedOn,
      claimSummary: invoice?.invoiceSummary,
      productCode: claimData?.productCode,
      jbwAgreementId: claimData?.jbwAgreementId,
      customerName: invoice ? `${invoice.firstName} ${invoice.lastName}` : '',
      addressLine1: invoice?.addressLine1,
      addressLine2: invoice?.addressLine2,
      city: invoice?.city,
      state: invoice?.state,
      zipcode: invoice?.zipcode,
      claimStatus: 'Pending',
      createdOn: now,
      createdBy: user?.name || user?.username || user?.email,
      modifiedOn: now,
      modifiedBy: user?.name || user?.username || user?.email,
    }

    setSaving(true)
    setSaveError('')
    createPotentialClaim(payload,invoice?.dealerId)
      .then(() => {
        setSaving(false)
        handleClose()
      })
      .catch((err) => {
        setSaving(false)
        setSaveError(err?.response?.data?.message || 'Failed to create potential claim. Please try again.')
      })
  }

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogTitle>Create Potential Claim</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError('')}>
              {saveError}
            </Alert>
          )}

          {/* Invoice Details */}
          {invoice && (
            <Paper elevation={3} sx={{ p: 1 }}>
              <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, fontWeight: 'bold' }}>
                Invoice Details
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Invoice Details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell>Job Number</TableCell>
                      <TableCell>Customer Name</TableCell>
                      <TableCell>Phone &amp; Email</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>ST Created On</TableCell>
                      <TableCell>ST Modified On</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{invoice.invoiceId}</TableCell>
                      <TableCell>{invoice.invoiceStatus}</TableCell>
                      <TableCell>{invoice.invoiceSummary}</TableCell>
                      <TableCell>{invoice.jobNumber}</TableCell>
                      <TableCell>
                        {invoice.firstName} {invoice.lastName}
                      </TableCell>
                      <TableCell>
                        {invoice.phoneNumber && invoice.phoneNumber !== 'null'
                          ? invoice.phoneNumber
                          : ''}
                        {invoice.emailAddress && invoice.emailAddress !== 'null'
                          ? `, ${invoice.emailAddress}`
                          : ''}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                          {`${invoice.addressLine1 || ''}${invoice.addressLine2 && invoice.addressLine2 !== 'null' ? ', ' + invoice.addressLine2 : ''}${invoice.city && invoice.city !== 'null' ? ', ' + invoice.city : ''}${invoice.state && invoice.state !== 'null' ? ', ' + invoice.state : ''}${invoice.zipcode && invoice.zipcode !== 'null' ? ', ' + invoice.zipcode : ''}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {invoice.stCreatedOn && (
                          <JBWDateLabel inputDate={invoice.stCreatedOn} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {invoice.stModifiedOn && (
                          <JBWDateLabel inputDate={invoice.stModifiedOn} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Location Agreement (Claim Data) */}
          {claimData && (
            <Paper elevation={3} sx={{ p: 1 }}>
              <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, fontWeight: 'bold' }}>
                Agreement
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Location Agreement Details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Code</TableCell>
                      <TableCell>Agreement Id</TableCell>
                      <TableCell>Agreement Status</TableCell>
                      <TableCell>Agreement Link</TableCell>
                      <TableCell>Purchase Date</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Coverage Start Date</TableCell>
                      <TableCell>Expiration Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{claimData.productCode}</TableCell>
                      <TableCell>{claimData.jbwAgreementId}</TableCell>
                      <TableCell>{claimData.agreementStatus}</TableCell>
                      <TableCell>{claimData.agreementLink}</TableCell>
                      <TableCell>
                        {claimData.purchaseDate && (
                          <JBWDateLabel inputDate={claimData.purchaseDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {claimData.startDate && (
                          <JBWDateLabel inputDate={claimData.startDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {claimData.coverageStartDate && (
                          <JBWDateLabel inputDate={claimData.coverageStartDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                      <TableCell>
                        {claimData.expirationDate && (
                          <JBWDateLabel inputDate={claimData.expirationDate} dateFormat="MM/DD/YYYY" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PotentialClaimsCreateDialog
